<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SidebarNavigation from "../navigation/sidebarNavigation.vue";
import axios from "axios";
import { useSidebarState } from '../../composables/useSidebarState.js';

import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
} from '@headlessui/vue'

import { MenuAlt3Icon } from '@heroicons/vue/outline'
import IconUser from "../icons/iconUser.vue";

const {
    isCollapsed,
    isForcedCollapse,
    isEffectivelyExpanded,
    isOverlay,
    toggleCollapse,
    setForcedCollapse,
} = useSidebarState();

const props = defineProps({
    logoUrl: {
        type: String,
        required: false,
        default: null
    },
    userId: {
        type: Number,
        required: true,
    },
    userProfileImageUrl: {
        type: String,
        required: false,
        default: null,
    },
    navigationItems: Array,
    hiddenNavigationItems: {
        type: [Array, Object],
        required: false,
        default: () => ({ headers: [], items: [] }),
    },
    forceNavCollapsed: {
        type: Boolean,
        default: false,
    },
    forceFullWidth: {
        type: Boolean,
        default: false,
    },
})

watch(() => props.forceNavCollapsed, (val) => {
    setForcedCollapse(val);
}, { immediate: true });

const notificationCount = ref(0);
const inboxMessageCount = ref(0);

const dockToolComponent = ref(null);
const dockToolTransitioned = ref(false);

const dockTools = {
    'url-shortener': { component: 'utility-url-shortener', title: 'URL Shortener', openIn: 'popup' },
    'password-generator': { component: 'utility-password-generator', title: 'Password Generator', openIn: 'popup' },
    'qr-generator': { component: 'utility-qr-generator', title: 'QR Generator', openIn: 'popup' },
    'time-tracking': { component: 'time-tracking-form', title: 'Time Tracking', openIn: 'popup' },
    'media-upload': { component: 'media-upload', title: 'Media Upload', openIn: 'popup' },
    'tasks': { component: 'utility-manage-tasks', title: 'Tasks', openIn: 'sidebar' },
    'toolbar-add-lead': { component: 'toolbar-add-lead', title: 'Add Contact', openIn: 'sidebar' },
};

function handleDockAction(event) {
    const action = event.detail;

    if (action.startsWith('tool:')) {
        const toolKey = action.replace('tool:', '');
        const tool = dockTools[toolKey];
        if (tool) {
            if (dockToolComponent.value?.component === tool.component) {
                dockToolComponent.value = null;
            } else {
                dockToolComponent.value = tool;
                setTimeout(() => { dockToolTransitioned.value = true; }, 1);
            }
        }
    } else if (action.startsWith('slideout:')) {
        const componentName = action.replace('slideout:', '');
        const tool = dockTools[componentName];
        if (tool) {
            dockToolComponent.value = tool;
        }
    }
}

watch(dockToolComponent, (val) => {
    if (!val) dockToolTransitioned.value = false;
});
const showInboxSlideout = ref(false);
const showNotificationSlideout = ref(false);
const delegatedGrants = ref([]);
const delegatedActionId = ref(null);
const showDelegatedModal = ref(false);
const delegatedBannerError = ref(null);
const hasUpgradeTopBanner = ref(false);
const isTeleporting = ref(false);
const showTeleportOverlay = ref(false);
const teleportLabel = ref('');

const mobileMenuOpen = ref(false)
const delegatedSession = computed(() => window.myflow?.delegatedAccess ?? { switched: false });
const hasDelegatedAccess = computed(() => delegatedGrants.value.length > 0);
const showDelegatedTopBar = computed(() => delegatedSession.value.switched);
const delegatedContextCompanyName = computed(() => window.myflow?.company?.name ?? '');
const normalizedNavigationItems = computed(() => (
    Array.isArray(props.navigationItems) ? props.navigationItems : []
));
const normalizedHiddenNavigationItems = computed(() => {
    const value = props.hiddenNavigationItems;

    if (value && typeof value === 'object' && ! Array.isArray(value)) {
        return {
            headers: Array.isArray(value.headers) ? value.headers : [],
            items: Array.isArray(value.items) ? value.items : [],
        };
    }

    return {
        headers: [],
        items: Array.isArray(value) ? value : [],
    };
});

const loadNotifications = async () => {
    let response = await axios.get(route('notifications'));
    notificationCount.value = response.data.unread_count;
}

const setNotificationCount = (count) => {
    notificationCount.value = count;
}

const openNotifications = () => {
    showNotificationSlideout.value = true
}

const openInboxSlideout = () => {
    // This is disabled until we have multiple inboxes
    // showInboxSlideout.value = true
    window.location.href = route('messages.inbox');
}

const openDelegatedSwitcher = () => {
    if (!hasDelegatedAccess.value || isTeleporting.value) {
        return;
    }

    delegatedBannerError.value = null;
    showDelegatedModal.value = true;
};

const closeDelegatedSwitcher = () => {
    showDelegatedModal.value = false;
};

const handleDelegatedSwitcherRequest = () => {
    openDelegatedSwitcher();
};

const logout = async () => {
    try {
        await axios.post(route('logout'));
    } catch (e) {
        // ignore — we're navigating to /login regardless
    }
    window.location.replace(route('login'));
};

const markAllAsRead = async () =>{
    axios.post( route('notification.markAllAsRead' ));
    loadNotifications();
    showNotificationSlideout.value = false;
};

const loadDelegatedAccess = async () => {
    try {
        const response = await axios.get(route('company.user.delegated-access.received'));
        delegatedGrants.value = response.data.grants ?? [];
    } catch (error) {
        delegatedGrants.value = [];
    }
};

const wait = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});

const runTeleportTransition = async ({ label, request, onError }) => {
    if (isTeleporting.value) {
        return;
    }

    isTeleporting.value = true;
    showTeleportOverlay.value = true;
    teleportLabel.value = label;

    try {
        const responsePromise = request();
        await wait(700);
        const response = await responsePromise;
        await wait(500);
        window.location.href = response.data.redirect ?? route('boIndex');
    } catch (error) {
        onError(error);
        showTeleportOverlay.value = false;
        teleportLabel.value = '';
        isTeleporting.value = false;
    }
};

const switchDelegatedAccess = async (grant) => {
    showDelegatedModal.value = false;
    delegatedActionId.value = grant.id;
    delegatedBannerError.value = null;

    await runTeleportTransition({
        label: _mft('navigation:backOffice.widget.teleportSwitching', {
            company: grant.company_name,
        }),
        request: () => axios.post(route('company.user.delegated-access.switch', grant.id)),
        onError: (error) => {
            showDelegatedModal.value = true;
            delegatedActionId.value = null;
            delegatedBannerError.value = error.response?.data?.message_key
                ? _mft(error.response.data.message_key, error.response.data.message_args ?? {})
                : _mft('company:delegatedAccess.error.generic');
        },
    });
};

const returnToOrigin = async () => {
    delegatedActionId.value = -1;
    delegatedBannerError.value = null;

    await runTeleportTransition({
        label: _mft('navigation:backOffice.widget.teleportReturning'),
        request: () => axios.post(route('company.user.delegated-access.return')),
        onError: (error) => {
            delegatedActionId.value = null;
            delegatedBannerError.value = error.response?.data?.message_key
                ? _mft(error.response.data.message_key, error.response.data.message_args ?? {})
                : _mft('company:delegatedAccess.error.generic');
        },
    });
};

watch(mobileMenuOpen, (val) => {
    if( val === false ){
        loadNotifications()
    }
}, {deep: true})

onMounted(() => {
    hasUpgradeTopBanner.value = document.getElementById('upgrade-top-banner') !== null;
    loadNotifications();
    loadDelegatedAccess();
    window.addEventListener('delegated-access:open-switcher', handleDelegatedSwitcherRequest);
    window.addEventListener('dock-action', handleDockAction);
})

onBeforeUnmount(() => {
    window.removeEventListener('delegated-access:open-switcher', handleDelegatedSwitcherRequest);
    window.removeEventListener('dock-action', handleDockAction);
})
</script>
<template>
    <div class="bg-gray-100 h-full">
        <div
            v-if="showDelegatedTopBar"
            class="border-b border-slate-700 bg-slate-900 text-white"
            :class="hasUpgradeTopBanner ? '' : 'mt-16 md:mt-0'"
        >
            <div class="flex items-center justify-end gap-2 px-4 py-2">
                <div class="flex items-center gap-2">
                    <div class="text-xxs md:text-xs text-gray-400">
                        {{ _mft('navigation:backOffice.widget.delegatedBannerSwitched', { company: delegatedContextCompanyName }) }}
                    </div>
                    <button-base
                        v-if="delegatedSession.switched"
                        style-type="secondary"
                        :disabled="delegatedActionId === -1 || isTeleporting"
                        @click="returnToOrigin"
                        classes="!bg-transparent !text-myflowLavender-light"
                    >
                        {{ _mft('navigation:backOffice.widget.returnToMyAccount') }}
                    </button-base>
                </div>
            </div>
            <div v-if="delegatedBannerError" class="px-4 pb-2 text-xs text-red-200">
                {{ delegatedBannerError }}
            </div>
        </div>

        <div class="flex pt-16 md:pt-0">
            <div
                id="navigation-bar"
                class="hidden md:flex flex-col fixed top-0 bottom-0 left-0 z-sidebar bg-white border-r border-gray-200 transition-all duration-200"
                :class="[
                    isEffectivelyExpanded ? 'w-60' : 'w-16',
                    isOverlay ? 'shadow-2xl' : 'shadow-sm',
                ]"
            >
                <button
                    type="button"
                    @click="toggleCollapse"
                    class="absolute top-10 -right-3 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white border border-gray-200 shadow-sm text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors"
                    :title="isEffectivelyExpanded ? _mft('navigation:sidebar.collapse') : _mft('navigation:sidebar.expand')"
                >
                    <i
                        class="fa-light fa-chevron-left text-xs transition-transform duration-200"
                        :class="!isEffectivelyExpanded ? 'rotate-180' : ''"
                    ></i>
                </button>
                <div class="h-full flex flex-col">
                    <!-- Fixed-height header so nav items keep their position when toggling -->
                    <div class="flex items-center flex-shrink-0 w-full h-12 pl-3 pr-2 mt-2 mb-5">
                        <a
                            :href="route('catchAll')"
                            title="Go to Dashboard"
                            class="flex items-center h-full"
                        >
                            <img
                                v-if="logoUrl"
                                :src="logoUrl"
                                alt="Logo"
                                class="object-contain object-left h-12 transition-all duration-200"
                                :class="isEffectivelyExpanded ? 'w-24' : 'w-10'"
                            />
                            <logo-icon
                                v-else
                                class="w-auto h-8 text-wlPrimary"
                            />
                        </a>
                    </div>

                    <SidebarNavigation
                        class="md:flex-1 md:overflow-y-auto md:w-full"
                        :items="normalizedNavigationItems"
                        :hidden-items="normalizedHiddenNavigationItems"
                        :show-labels="isEffectivelyExpanded"
                        :notification-count="notificationCount"
                        :user-profile-image-url="userProfileImageUrl"
                        @open-notifications="openNotifications"
                    />
                </div>
            </div>
            <!-- Mobile top bar -->
            <div class="fixed top-0 left-0 right-0 flex items-center justify-between w-full px-4 py-2 bg-white/30 backdrop-blur-lg z-sidebar md:hidden border-b border-b-gray-200">
                <a :href="route('catchAll')" title="Go to Dashboard">
                    <img
                        v-if="logoUrl"
                        :src="logoUrl"
                        alt="Logo"
                        class="object-contain object-left w-24 h-12"
                    />
                    <logo-icon
                        v-else
                        class="w-auto h-8 mr-4 text-wlPrimary"
                    />
                </a>
                <div class="flex">
                    <button
                        type="button"
                        class="relative ml-2 text-gray-500 group focus:outline-none"
                        @click="mobileMenuOpen = !mobileMenuOpen"
                    >
                        <div class="relative w-6 h-6">
                            <div
                                class="absolute flex items-center justify-center w-5 h-5 text-xs text-white transition rounded-full -top-2 -right-2 count bg-wlPrimary group-hover:bg-wlPrimary-light group-hover:text-wlPrimaryContrast-light group-focus:bg-wlPrimary-light group-focus:text-white"
                                v-if="notificationCount > 0"
                            >
                                {{ notificationCount }}
                            </div>
                            <span class="sr-only">{{ _mft('navigation:backOffice.openSidebar') }}</span>
                            <MenuAlt3Icon class="w-6 h-6 stroke-current group-focus:text-wlPrimary" aria-hidden="true" />
                        </div>
                    </button>
                </div>
            </div>

            <!-- Mobile sidebar slide-out -->
            <SidebarNavigation
                class="fixed top-0 bottom-0 py-4 overflow-y-scroll transition-transform transform right-full w-64 z-sidebar md:hidden"
                :class="mobileMenuOpen ? 'translate-x-full bg-gray-100' : ''"
                :items="normalizedNavigationItems"
                :hidden-items="normalizedHiddenNavigationItems"
                :show-labels="true"
                :notification-count="notificationCount"
                :user-profile-image-url="userProfileImageUrl"
                @open-notifications="openNotifications"
            />
            <basic-slideout v-model:open="showNotificationSlideout">
                <template #header>
                    <div>
                        <h1>{{ _mft('notification:domainNamePlural') }}</h1>
                        <p v-if="notificationCount > 0" class="text-black">
                            {{
                                _mft('notification:nUnreadNotifications', {
                                    count: notificationCount,
                                })
                            }}
                        </p>
                    </div>
                </template>
                <template #body>
                    <notification-index
                        :maxcount="300"
                        @update="setNotificationCount"
                    />
                </template>
                <template #footer>
                    <button-secondary size="sm" @click="markAllAsRead">
                        {{ _mft('notification:markAllAsRead') }}
                    </button-secondary>
                </template>
            </basic-slideout>
            <modal-basic
                :open="showDelegatedModal"
                @closed="closeDelegatedSwitcher"
            >
                <template #header>
                    {{ _mft('company:delegatedAccess.received.title') }}
                </template>
                <template #body>
                    <p class="mb-4 text-sm text-gray-600">
                        {{ _mft('company:delegatedAccess.received.description') }}
                    </p>
                    <p v-if="delegatedBannerError" class="mb-3 text-xs text-red-600">
                        {{ delegatedBannerError }}
                    </p>

                    <ul class="divide-y">
                        <li
                            v-for="grant in delegatedGrants"
                            :key="`delegated-modal-grant-${grant.id}`"
                            class="flex items-center justify-between gap-3 py-3"
                        >
                            <div class="min-w-0">
                                <p class="font-semibold text-gray-900 truncate">
                                    {{ grant.company_name }}
                                </p>
                                <p class="text-xs text-gray-500">
                                    {{ _mft('company:delegatedAccess.received.grantedBy') }}: {{ grant.granted_by_name }}
                                </p>
                            </div>
                            <button
                                type="button"
                                class="rounded-md bg-wlPrimary px-3 py-1.5 text-xs font-semibold text-wlPrimaryContrast hover:bg-wlPrimary-light disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="delegatedActionId === grant.id || isTeleporting"
                                @click="switchDelegatedAccess(grant)"
                            >
                                {{ _mft('company:delegatedAccess.action.switch') }}
                            </button>
                        </li>
                        <li v-if="delegatedGrants.length === 0" class="py-3 text-sm text-gray-500">
                            {{ _mft('company:delegatedAccess.received.empty') }}
                        </li>
                    </ul>
                </template>
            </modal-basic>

            <transition name="teleport-fade">
                <div
                    v-if="showTeleportOverlay"
                    class="teleport-overlay"
                >
                    <div class="teleport-vortex">
                        <span class="teleport-ring teleport-ring-a"></span>
                        <span class="teleport-ring teleport-ring-b"></span>
                        <span class="teleport-ring teleport-ring-c"></span>
                        <span class="teleport-core"></span>
                    </div>
                    <p class="teleport-label">{{ teleportLabel }}</p>
                </div>
            </transition>

            <!-- Content area -->
            <main
                class="flex-1 transition-all duration-200"
                :class="[
                    isForcedCollapse || isCollapsed ? 'md:ml-16' : 'md:ml-60',
                ]"
            >
                <!-- Primary column -->
                <section
                    aria-labelledby="primary-heading"
                    class="z-20 flex flex-col flex-1 h-full min-w-0 min-h-contentarea lg:order-last"
                >
                    <div
                        id="teleport-target-sticky-before-main"
                        class="top-0 w-full mx-auto md:sticky z-topbar"
                    ></div>
                    <div
                        class="w-full mx-auto"
                        :class="forceFullWidth ? '' : 'max-w-7xl'"
                    >
                        <slot></slot>
                    </div>
                </section>
            </main>
        </div>

        <!-- Dock tool popup -->
        <div
            v-if="dockToolComponent && dockToolComponent.openIn === 'popup'"
            class="fixed inset-0 flex items-center justify-center z-modal-backdrop"
        >
            <div
                class="fixed inset-0 bg-black/20 transition-opacity"
                :class="dockToolTransitioned ? 'opacity-100' : 'opacity-0'"
                @click="dockToolComponent = null"
            ></div>
            <card-basic
                class="relative w-full max-w-sm transition-all z-modal"
                :class="dockToolTransitioned ? '' : 'opacity-0 scale-95'"
            >
                <div class="pt-2">
                    <h2 v-if="dockToolComponent.title">{{ dockToolComponent.title }}</h2>
                    <component
                        :is="dockToolComponent.component"
                        @close="dockToolComponent = null"
                    ></component>
                </div>
            </card-basic>
        </div>

        <!-- Dock tool slideout -->
        <slideout-with-slots
            v-if="dockToolComponent && dockToolComponent.openIn === 'sidebar'"
            @close="dockToolComponent = null"
        >
            <template #title>
                <h2>{{ dockToolComponent.title }}</h2>
            </template>
            <template #body>
                <component
                    :is="dockToolComponent.component"
                    @close="dockToolComponent = null"
                ></component>
            </template>
        </slideout-with-slots>

        <div id="teleport-target-modal"></div>
        <time-tracking-modal />
        <flash-messages />
        <tooltip-host />
    </div>
</template>
<style scoped>
.min-h-contentarea {
    min-height: 100vh;
}

.teleport-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background:
        radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 0, rgba(255, 255, 255, 0) 40%),
        radial-gradient(circle at 75% 70%, rgba(145, 102, 160, 0.25) 0, rgba(145, 102, 160, 0) 45%),
        linear-gradient(145deg, rgba(8, 15, 45, 0.95), rgba(23, 5, 56, 0.96));
    backdrop-filter: blur(3px);
}

.teleport-vortex {
    position: relative;
    width: 10rem;
    height: 10rem;
}

.teleport-ring {
    position: absolute;
    inset: 0;
    border: 2px solid rgba(255, 255, 255, 0.45);
    border-radius: 9999px;
    mix-blend-mode: screen;
}

.teleport-ring-a {
    animation: teleportRing 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.teleport-ring-b {
    animation: teleportRing 1.1s cubic-bezier(0.4, 0, 0.2, 1) 0.2s infinite;
}

.teleport-ring-c {
    animation: teleportRing 1.1s cubic-bezier(0.4, 0, 0.2, 1) 0.4s infinite;
}

.teleport-core {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 9999px;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(255, 255, 255, 0.96), rgba(212, 182, 255, 0.7));
    box-shadow: 0 0 22px rgba(210, 182, 255, 0.85);
    animation: teleportCore 0.9s ease-in-out infinite;
}

.teleport-label {
    margin-top: 1.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: #f8f9ff;
    text-shadow: 0 0 14px rgba(205, 175, 255, 0.55);
}

.teleport-fade-enter-active,
.teleport-fade-leave-active {
    transition: opacity 180ms ease;
}

.teleport-fade-enter-from,
.teleport-fade-leave-to {
    opacity: 0;
}

@keyframes teleportRing {
    0% {
        transform: scale(0.55);
        opacity: 0.95;
    }
    100% {
        transform: scale(1.35);
        opacity: 0;
    }
}

@keyframes teleportCore {
    0%, 100% {
        transform: translate(-50%, -50%) scale(0.85);
    }
    50% {
        transform: translate(-50%, -50%) scale(1.15);
    }
}
</style>
